import { Grid } from '@mui/material';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import Background from '../../assets/img/onboarding/backgrounds/background.png';
import { OnboardingContent } from '../../pages/onboarding/Onboarding.styled';
import { AnimatedRightStep } from '../../pages/onboarding/steps/AnimatedRightStep';
import { AnimatedLeftStep } from '../../pages/onboarding/steps/AnimatedStep';
import { StepsTopBar } from '../../pages/onboarding/steps/StepsTopBar';

const LeftContainer = styled(Grid)<{ width: number }>`
  height: 100%;
  min-width: 500px;
  width: ${(props) => props.width}% !important;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 10px;

  > * {
    border-radius: 20px;
  }
`;

export interface OnboardingStepProps {
  step: number;
  totalSteps: number;
  backButton: React.ReactNode;
  nextButton: React.ReactNode;
}

interface FloatingLayoutProps {
  leftAnimation?: boolean;
  left?: React.ReactNode;
  leftWidthPercentage?: number;
  leftStyle?: React.CSSProperties;
  right: React.ReactNode;
  background?: React.ReactNode;
  containerStyle?: React.CSSProperties;
  success?: boolean;
  onNextStep?: () => void;
  onboardingStepProps?: OnboardingStepProps;
}

export const FloatingLayout = ({
  leftAnimation = true,
  left,
  leftWidthPercentage = 40,
  leftStyle,
  right,
  background,
  containerStyle,
  success,
  onNextStep,
  onboardingStepProps,
}: FloatingLayoutProps) => {
  useEffect(() => {
    if (success && onNextStep) {
      setTimeout(() => {
        onNextStep();
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const leftContent = () => {
    return (
      <LeftContainer width={leftWidthPercentage} style={leftStyle}>
        {onboardingStepProps ? (
          <Grid
            container
            direction="row"
            wrap="nowrap"
            style={{
              height: `100%`,
              width: `100%`,
              position: `relative`,
              padding: `20px`,
              backgroundColor: RoutalPalette.neutral02n,
            }}
            className={leftAnimation ? `animate__animated animate__fadeIn animate__faster` : undefined}
          >
            <StepsTopBar step={onboardingStepProps.step} totalSteps={onboardingStepProps.totalSteps} />
            <Grid item container direction="column" wrap="nowrap" style={{ position: `relative`, height: `100%` }}>
              <OnboardingContent container direction="column" justifyContent="center" alignItems="center" wrap="nowrap">
                <AnimatedLeftStep
                  id="floatingLayoutAnimatedContainerLeft"
                  isVisible={!success}
                  disabled={!leftAnimation}
                >
                  <>{left}</>
                </AnimatedLeftStep>
              </OnboardingContent>

              {onboardingStepProps.backButton}

              {onboardingStepProps.nextButton}
            </Grid>
          </Grid>
        ) : (
          <AnimatedLeftStep id="floatingLayoutAnimatedContainerLeft" isVisible={!success} disabled={!leftAnimation}>
            <>{left}</>
          </AnimatedLeftStep>
        )}
      </LeftContainer>
    );
  };

  return (
    <Grid
      sx={{
        height: `100vh`,
        width: `100vw`,
        display: `flex`,
        flexDirection: `row`,
        justifyContent: `center`,
      }}
      style={containerStyle}
    >
      <Grid
        style={{
          position: `absolute`,
          inset: 0,
          backgroundImage: `url(${Background})`,
          backgroundSize: `cover`,
        }}
      >
        {background}
      </Grid>
      {left && leftContent()}
      <Grid
        sx={{
          height: `100%`,
          width: `${100 - (left ? leftWidthPercentage : 0)}%`,
          display: `flex`,
          flexDirection: `row`,
          justifyContent: `center`,
          alignItems: `center`,
        }}
      >
        <AnimatedRightStep id="floatingLayoutAnimatedContainerRight" isVisible={!success}>
          <>{right}</>
        </AnimatedRightStep>
      </Grid>
    </Grid>
  );
};
